import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Separator } from './ui/separator';
import { Mail, MapPin, Phone, Zap } from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';

export const Footer = () => {
  const { t, i18n } = useTranslation();

  const isSpanish = i18n.language === 'es';
  const langPrefix = isSpanish ? '/es' : '/fr';
  const country = isSpanish ? 'ES' : 'FR';
  
  // Détecter le vertical depuis l'URL
  const currentPath = window.location.pathname;
  const isInternetPage = currentPath.includes('/fournisseur/offre-internet') || 
                         currentPath.includes('/fournisseur/offres-internet') ||
                         currentPath.includes('/fournisseur/comparer-internet') ||
                         currentPath.includes('/fournisseur/tarif-internet') ||
                         currentPath.includes('/proveedor/oferta-internet') ||
                         currentPath.includes('/proveedor/ofertas-internet') ||
                         currentPath.includes('/proveedor/comprar-internet') ||
                         currentPath.includes('/proveedor/comparar-internet') ||
                         currentPath.includes('/proveedor/tarifa-internet') ||
                         currentPath.includes('/proveedor/tarifas-internet') ||
                         currentPath.includes('/proveedor/contrato-internet');
  
  const vertical = isInternetPage ? 'internet' : 'electricity';
  
  const specialist = getSpecialistMessage(country, vertical);
  const saveaPhone = getSaveaPhone(country, vertical, 'LP');
  const saveaPhoneTel = getSaveaPhoneTel(country, vertical, 'LP');

  const footerLinks = [
    { key: 'about', path: `${langPrefix}/${isSpanish ? 'acerca-de' : 'a-propos'}`, label: isSpanish ? 'Acerca de' : 'À Propos' },
    { key: 'faq', path: `${langPrefix}/faq`, label: 'FAQ' },
    { key: 'contact', path: `${langPrefix}/${isSpanish ? 'contacto' : 'contact'}`, label: isSpanish ? 'Contacto' : 'Contact' },
    { key: 'methodology', path: `${langPrefix}/${isSpanish ? 'metodologia' : 'methodologie'}`, label: isSpanish ? 'Metodología' : 'Méthodologie' },
    { key: 'privacy', path: `${langPrefix}/${isSpanish ? 'privacidad' : 'confidentialite'}`, label: isSpanish ? 'Política de privacidad' : 'Politique de confidentialité' },
    { key: 'terms', path: `${langPrefix}/${isSpanish ? 'terminos' : 'conditions'}`, label: isSpanish ? 'Términos de uso' : 'Conditions d\'utilisation' },
    { key: 'cookies', path: `${langPrefix}/cookies`, label: isSpanish ? 'Gestión de cookies' : 'Gestion des cookies' }
  ];

  return (
    <footer className="bg-gradient-to-br from-sage-50 to-sage-100 border-t border-sage-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-sage-600 to-sage-700 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-2xl text-sage-800">SAVEA</span>
            </div>
            <p className="text-sage-600 mb-4 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="space-y-2 text-sm text-sage-600">
              <p className="font-medium">{t('footer.company')}</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-sage-800 mb-4">{t('footer.navigation')}</h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to={langPrefix} 
                  className="text-sage-600 hover:text-sage-800 transition-colors text-sm"
                  data-testid="footer-link-home"
                >
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link 
                  to={`${langPrefix}/${isSpanish ? 'herramientas' : 'outils'}`} 
                  className="text-sage-600 hover:text-sage-800 transition-colors text-sm"
                  data-testid="footer-link-tools"
                >
                  {t('nav.tools')}
                </Link>
              </li>
              <li>
                <Link 
                  to={`${langPrefix}/ebooks`} 
                  className="text-sage-600 hover:text-sage-800 transition-colors text-sm"
                  data-testid="footer-link-ebooks"
                >
                  {t('nav.ebooks')}
                </Link>
              </li>
              <li>
                <Link 
                  to={`${langPrefix}/${isSpanish ? 'modelos-email' : 'modeles-emails'}`} 
                  className="text-sage-600 hover:text-sage-800 transition-colors text-sm"
                  data-testid="footer-link-templates"
                >
                  {t('nav.templates')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold text-sage-800 mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.key}>
                  <Link 
                    to={link.path} 
                    className="text-sage-600 hover:text-sage-800 transition-colors text-sm"
                    data-testid={`footer-link-${link.key}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-8 bg-sage-200" />

        {/* Bottom Footer */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-sage-600 text-center sm:text-left">
            <p className="mb-1">
              © 2026 SAVEA - {isSpanish ? 'Todos los derechos reservados' : 'Tous droits réservés'}
            </p>
            <p className="text-xs">{t('footer.disclaimer')}</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-xs text-sage-500">
              SOLUCIONES INTEGRALES HORIZON, S.L.U.
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-8 p-6 bg-gradient-to-r from-sage-600 to-sage-700 rounded-2xl text-center">
          <h3 className="text-white font-semibold text-lg mb-2">
            {t('footer.help_cta')}
          </h3>
          <p className="text-sage-100 text-sm mb-4">
            {t('footer.help_desc')}
          </p>
          <a
            href={`tel:${saveaPhoneTel}`}
            className="inline-flex items-center gap-2 bg-white text-sage-700 px-6 py-2 rounded-full font-medium hover:bg-sage-50 transition-colors cursor-pointer"
            data-testid="footer-cta-contact"
          >
            <Phone className="w-4 h-4" />
            {isSpanish ? 'Hablar con Especialista' : 'Parler à un Spécialiste'}
          </a>
        </div>
      </div>
    </footer>
  );
};